import type { Locale } from '@mfw/translations';

import { getLocalizedNameByLocale } from '@mfw/components';
import { v4 as uuidv4 } from 'uuid';
import omit from 'lodash/omit';

import type {
  UiExerciseTranslation,
  ApiExerciseTranslation,
  UiExerciseCategories,
  UiCreateExercise,
  ApiExercise,
  UiExercise,
} from '../../schemas/exercises.schema';
import type { ApiContentConfigurationPayload } from '../../schemas/contentConfiguration.schema';

import {
  mapContentConfigurationFromApi,
  mapContentConfigurationToApi,
} from './contentConfiguration.mappers';
import { mapConfigurableFromApi } from './configurables.mappers';
import { mapTranslationsFromApi } from './translations.mappers';
import { DEFAULT_CURRENCY } from '../../config/constants';
import { getLabelFromTranslations } from '../../utils';

export function createExerciseApiArgs(
  exercise: Omit<
    UiCreateExercise,
    'coverFile' | 'coverSignedUrl' | 'coverSource' | 'coverType' | 'mediaFile'
  > & {
    coverSignedUrl: null | string;
    coverSource: null | string;
    coverType: null | string;
  },
) {
  let tags: string[] = [];

  const contentConfiguration: ApiContentConfigurationPayload =
    mapContentConfigurationToApi(exercise);

  if (exercise.categories && Object.keys(exercise.categories).length) {
    tags = Object.values(exercise.categories).flatMap((category) => {
      return category?.flatMap((tag) => tag.value) ?? [];
    });
  }

  return omit(
    {
      ...exercise,
      ...contentConfiguration,
      tags,
      translations: exercise.translations.map((translation) => ({
        description: translation.description,
        locale: translation.locale?.[0]?.value,
        name: translation.name,
      })),
    },
    [
      'categories',
      'coverFile',
      'mediaFile',
      'coverSignedUrl',
      'mediaSignedUrl',
      'locale',
      'isOrg',
      'organizationIds',
      'isMarketplace',
      'marketplaceCurrency',
      // form metadata for the 9.6 price floor — never part of the payload
      'marketplaceListedPrice',
      'marketplacePrice',
    ],
  );
}

/**
 * One row per API translation, each carrying exactly ONE locale entry.
 *
 * The return type is annotated rather than inferred, and that is the point:
 * `UiTranslationSchema.locale` is `.nonempty()`, so every UI schema built on it
 * types `locale` as a NON-EMPTY TUPLE. A bare array literal infers as a plain
 * array, which is assignable to nothing that expects the tuple — that mismatch
 * is what `workouts.mappers.ts` used to suppress with a `@ts-ignore` on its
 * whole `groups` block. The literal below always has one element, so the tuple
 * is the honest type; it just has to be asked for.
 */
export function mapExerciseTranslationsFromApi(
  translations?: Array<ApiExerciseTranslation>,
): Array<UiExerciseTranslation & { id: string }> {
  if (!translations) return [];
  return translations?.map((translation) => ({
    id: translation.id,
    description: translation.description,
    locale: [
      {
        label: getLocalizedNameByLocale(translation.locale),
        value: translation.locale as Locale,
      },
    ],
    name: translation.name,
  }));
}

export function mapExerciseFromApi(
  exercise: ApiExercise,
  locale: Locale,
): UiExercise {
  const categories: UiExerciseCategories = {};

  if (exercise.categories && exercise.categories.length) {
    exercise.categories.forEach((category) => {
      // TODO Add tags
      categories[category.id] = category.tags.map((tag) => {
        return {
          label: getLabelFromTranslations(
            locale,
            mapTranslationsFromApi(tag.translations),
          ),
          value: tag.id,
        };
      });
    });
  }

  return {
    id: exercise.id,
    allowAutoCoverExtract: exercise.allowAutoCoverExtract,
    author: {
      id: exercise?.author?.id,
      name: exercise?.author?.name,
    },
    categories,
    comment: exercise?.comment ?? null,
    configurables: (exercise?.configurables as any)?.map(
      mapConfigurableFromApi,
    ),
    // read-only, catalog flags included — the preview mount reads them
    // (`usePreviewOrganizationId`); a save replaces it (`createExerciseApiArgs`)
    configuration: mapContentConfigurationFromApi(exercise),
    coverSignedUrl: exercise.coverSignedUrl,
    coverSource: exercise.coverSource,
    coverType: exercise.coverType,
    createdAt: exercise.createdAt,
    isDeleted: exercise.isDeleted,
    // The JOIN, not the denormalised column: `Exercises.isMarketplace` drifts
    // (`trg_sync_is_marketplace` fires on `Marketplaces` writes only), and a
    // stale `true` labelled the kebab "Remove from marketplace" for a listing
    // that no longer exists. Every sibling mapper already reads this field.
    isMarketplace: exercise?.configuration?.marketplace?.enabled ?? false,
    isOrg: exercise?.configuration?.isOrg,
    level: exercise.level,
    marketplaceCurrency:
      exercise?.configuration?.marketplace?.currency ?? DEFAULT_CURRENCY,
    marketplaceId: exercise?.configuration?.marketplace?.id ?? null,
    marketplaceInLibrary:
      exercise?.configuration?.marketplace?.inLibrary ?? false,
    marketplaceRestorable:
      exercise?.configuration?.marketplace?.restorable ?? false,
    marketplaceLibraryId:
      exercise?.configuration?.marketplace?.libraryId ?? null,
    marketplacePrice: exercise?.configuration?.marketplace?.enabled
      ? (exercise?.configuration?.marketplace?.price ?? 0)
      : null,
    mediaSignedUrl: exercise.mediaSignedUrl,
    mediaSource: exercise.mediaSource,
    mediaType: exercise.mediaType,
    organizationId: exercise.organizationId,
    organizationIds: [],
    tenantId: exercise.tenantId,
    translations: exercise.translations?.map((translation) => ({
      id: translation.id,
      description: translation.description,
      locale: [
        {
          label: getLocalizedNameByLocale(translation.locale),
          value: translation.locale as Locale,
        },
      ],
      name: translation.name,
    })),
    uniqueId: uuidv4(),
    updatedAt: exercise.updatedAt,
  };
}
